/** @module SiteAdmin/Cards/Setup */
define([
	"dojo/_base/declare",
	"underscore",
	"csi",
	"csi/card/_base",
	"csi/card/AttributeCard",
	"csi/comm",
	"csi/def/Action",
	"csi/heading/Renderer",
	"csi/html",
	"csi/html/Action",
	"csi/html/Widget",
	"csi/prefs",
	"csi/service/businessObjectService",
	"csi/service/viewService",
	"csi/sessionConstants",
	"SiteAdmin/BusinessObject"
], function(declare, _, csi, csiCard, AttributeCard, comm, Action, Renderer, html, HtmlAction,
	HtmlWidget, csiPrefs, {canShowAttribute}, {getAttributeCardSpecsForType, getClassEmptyPropertyView}, sessionConstants, BusinessObject) {

	// TODO: views per ProductType/Subtype?

	const CONTEXT = "context";
	const DETAILED = "detailed";
	const SLIM = "slim";

	const _CustomRenderer = declare(Renderer, {
		/**
		 * @override
		 * Stubs do not need any extra CSS
		 */
		_getRenderCSS: function(ctx, h, refNode) {
			return null;
		},

		/**
		 * @override
		 * Don't consider dependencies when rendering stubs
		 */
		hasValidDependency: function(ctx, h) {
			return true;
		},

		/**
		 * @override
		 * Render empty span that can be styled to design
		 */
		toHtmlStatic: function(ctx, h, refNode, actionData) {
			html.create("span", {innerText: " "}, refNode);
			return true;
		}
	});

	return declare(HtmlWidget, {
		/** @property {boolean} useMaster - whether to adjust and use master class instead of revision for cards */
		useMaster: false,

		/** @property {Class} _cardClass - reflection class for type being setup */
		_cardClass: null,

		/** @property {string} _cardContextViewId - id of child context view */
		_cardContextViewId: null,

		/** @property {string} _cardCustomViewUrl - url for custom view holding keys */
		_cardCustomViewUrl: null,

		/**
		 * Hide context view image when class does not support an image 
		 */
		onViewCreate: function() {
			const useType = this._cardClass.getType();
			const cardSpecs = csiCard.getAttributeCardSpecs(useType);

			if (!cardSpecs.imageSpec) {
				$(this.domNode).addClass("csi-card-setup-no-image");
			}
		},

		/**
		 * Create slim or detailed attribute card at given location
		 * @param {DOMNode} domNode
		 * @param {string} cardType
		 */
		_createAttributeCard: function(domNode, cardType) {
			const ctx = this.getContext(csi.EMPTY_URL);
			const cardSpecs = this._getCurrentAttributeCardSpecs();
			const specs = cardSpecs.attributeSpecs;
			const imageSpec = cardSpecs.imageSpec;
			const defView = this.defView;
			const n = specs.length;
			const slim = (cardType === SLIM);

			// override first (and second if slim) specs' heading since setup node has no data - use labels instead
			specs[0].heading = _.extend(defView.newHeading(), {
				m_value: specs[0].getText()
			});

			if (slim && (n > 1)) {
				// determine secondary spec if slim card
				specs[1].heading = _.extend(defView.newHeading(), {
					m_value: specs[1].getText()
				});
			} else {
				// assign custom renderer to specs for detailed card (skip primary spec)
				for (var i = 1; i < n; ++i) {
					specs[i].heading.classRenderer = _CustomRenderer;
				}
			}

			// finally create card
			new AttributeCard({
				ctx: ctx,
				attributeSpecs: specs,
				imageSpec: imageSpec,
				slim: slim
			}).placeAt(domNode);
		},

		/**
		 * Insert context view into view
		 * @param {DOMNode} domNode
		 * @return {Deferred}
		 */
		_createAttributeContextView: function(domNode) {
			const defView = getClassEmptyPropertyView(this._cardClass);
			const cardSpecs = this._getCurrentAttributeCardSpecs();
			const attributeKeys = _.map(cardSpecs.attributeSpecs, (spec) => spec.uniqueId);

			this._cardContextViewId = defView.getId();
			this._insertAndCreateView(defView, null, domNode, {
				attributeKeys,
				disableCustomViewToolbar: true,
				displayPropertyImage: false,
				plugins: [],
				propertyColumns: 2,
				supportsCustomViews: () => true,
				urlDefaultView: csi.EMPTY_URL,
			});
		},

		/**
		 * Return specs for attribute card based on latest configuration
		 * @return {AttributeCardSpecs}
		 */
		_getCurrentAttributeCardSpecs() {
			const {cacheView, _cardClass, _cardCustomViewUrl} = this;
			const customView = cacheView.get(_cardCustomViewUrl);
			return getAttributeCardSpecsForType(_cardClass.getType(), customView);
		},

		/**
		 * @override
		 * Get custom view node holding attribute keys in root request too 
		*/
		_onRootRequest: function(request) {
			// TODO: use getValidatedCustomView
			request.addParameter(comm.QUERY_URL, [
				csiPrefs.getAttributeCardContainerUrl(),
				this._cardCustomViewUrl
			], true);

			this.inherited(arguments);
		},

		/**
		 * Pass a few parameters to edit action controller
		 * @param {Controller} ac
		 * @return {boolean}
		 */
		_preActionEditCard: function(ac) {
			ac.urlCustomView = this._cardCustomViewUrl;
			ac.urlCustomViewContainer = csiPrefs.getAttributeCardContainerUrl();
			ac.viewWithAttributes = this.getChildView(this._cardContextViewId);
			return true;
		},

		/**
		 * @override
		 * Custom processing of card keys
		 */
		_processKey: function(ctx, key, def, domNode) {
			switch (key) {
				case SLIM:
				case DETAILED:
					this._createAttributeCard(domNode, key);
					return;

				case CONTEXT:
					this._deferredFunctions.push(this._createAttributeContextView.bind(this, domNode));
					return;
			}

			return this.inherited(arguments);
		},

		/**
		 * @override
		 * Populate resources for view
		 */
		_resolveView: function() {
			const defView = this.defView;
			const rc = this.useMaster ? BusinessObject.getRootClass(this.getViewNode()) : BusinessObject.getAttributeClass(this.getViewNode());
			const type = rc.getType();

			this._cardClass = rc;
			this._cardCustomViewUrl = csiPrefs.getAttributeCardUrlForType(type);

			defView.addResource("edit", true, new HtmlAction(_.extend(new Action(), {
				m_id: "AttributeCardsEdit",
				m_accessAction: () => sessionConstants.configEditor,
				m_preAction: "_preActionEditCard",
				m_postAction: "postActionRedraw",
				classController: "csi/prefs/ManageAttributesController",
				classControllerParams: {
					canShowAttribute: canShowAttribute,
				}
			})));

			// these resources will be handled specially in _processKey
			defView.addResource(SLIM, true, {});
			defView.addResource(DETAILED, true, {});
			defView.addResource(CONTEXT, true, {});

			this.inherited(arguments);
		}
	});
});